import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileCheck, Film, Scale, XCircle, Image as ImageIcon, Rows3 } from "lucide-react";
import { FadeUp } from "@/components/motion";

export const AdGuidelines = ({ g, brandName = "SchoolsCompared.com", bg = "bg-secondary", showKicker = true, showJumbo = true }) => {
  // Hide Jumbo-flavoured placements + the Flexible Package (which exists only
  // to upgrade Elegant → Jumbo) when the Jumbo profile is reserved from view.
  const placements = showJumbo
    ? g.placements
    : (g.placements || []).filter((p) => p.profile !== "Jumbo");
  const showFlexible = showJumbo && g.flexible;

  // Split placements into header (Leaderboard) + in-article list for the new design
  const leaderboard = (placements || []).find((p) => /leaderboard/i.test(p.name));
  const inArticle = (placements || []).filter((p) => p !== leaderboard);

  return (
  <section data-testid={`ad-guidelines-${brandName}`} className={bg}>
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
      <FadeUp>
        {showKicker && <p className="kicker mb-4">Advertising Guidelines</p>}
        <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-3xl">
          Advertise on {brandName}
        </h2>
        {g.intro && <p className="mt-6 max-w-3xl text-lg leading-relaxed text-foreground/80">{g.intro}</p>}
      </FadeUp>

      {/* Placements + tech */}
      <div className={`mt-12 grid grid-cols-1 ${placements && placements.length ? "lg:grid-cols-5" : ""} gap-6`}>
        {placements && placements.length > 0 && (
          <FadeUp className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-border overflow-hidden h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between gap-3 px-6 pt-6 pb-5 border-b border-border">
                <p className="kicker">Ad Positions</p>
                <span className="font-mono text-[10px] uppercase tracking-widest text-foreground/50">
                  {placements.length} slot{placements.length === 1 ? "" : "s"}
                </span>
              </div>

              {/* Leaderboard — distinct, premium */}
              {leaderboard && (
                <div className="px-6 py-5 flex items-center gap-4 border-b border-border bg-gradient-to-r from-primary/[0.04] to-transparent" data-testid={`placement-${leaderboard.name}`}>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <ImageIcon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="headline text-lg text-ink leading-tight">{leaderboard.name}</p>
                    <p className="font-sans text-xs text-foreground/60 mt-0.5">Top of every article — the first thing readers see</p>
                  </div>
                  <span className={`shrink-0 font-sans text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${leaderboard.profile === "Jumbo" ? "bg-primary text-white" : "bg-ink text-white"}`}>
                    {leaderboard.profile}
                  </span>
                </div>
              )}

              {/* In-article positions */}
              {inArticle.length > 0 && (
                <div className="flex-1 flex flex-col">
                  <div className="px-6 pt-5 pb-2 flex items-center gap-2">
                    <Rows3 size={14} className="text-foreground/50" />
                    <p className="font-mono text-[10px] uppercase tracking-widest text-foreground/50">In-Article Positions</p>
                  </div>
                  <ul className="flex-1">
                    {inArticle.map((p, i) => {
                      const num = (p.name.match(/\d+/) || [i + 1])[0];
                      return (
                        <li key={p.name} className="px-6 py-3 flex items-center gap-4 border-t border-border/60 hover:bg-secondary/60 transition-colors" data-testid={`placement-${p.name}`}>
                          <span className="font-mono text-sm font-bold text-foreground/40 w-6 shrink-0">{String(num).padStart(2, "0")}</span>
                          <p className="headline text-base text-ink flex-1 min-w-0 truncate">{p.name}</p>
                          <span className={`shrink-0 font-sans text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${p.profile === "Jumbo" ? "bg-primary text-white" : "bg-ink text-white"}`}>
                            {p.profile}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Jumbo footer — signposts the special premium size and also stretches
                  the card so it aligns bottom-edge with the Tech Specs card. */}
              {!showJumbo && (
                <div className="px-6 py-4 border-t border-border bg-secondary/50 mt-auto">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-foreground/50 mb-1">
                    Jumbo
                  </p>
                  <p className="font-sans text-sm text-foreground/70 leading-relaxed">
                    Special Jumbo premium size.{" "}
                    <Link to="/contact?role=Jumbo%20Advertising%20Enquiry" className="font-bold text-ink hover:text-primary underline underline-offset-2">
                      Request more information
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </FadeUp>
        )}

        {g.tech && (
          <FadeUp className="lg:col-span-2">
            <div className="bg-ink text-white rounded-2xl overflow-hidden h-full flex flex-col">
              <div className="px-6 pt-6 pb-5 border-b border-white/10 flex items-center justify-between">
                <p className="kicker" style={{ color: "hsl(var(--primary))" }}>Technical Specs</p>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Required</span>
              </div>

              {/* The three big accept-rules — each a hero row */}
              <div className="divide-y divide-white/10">
                {[
                  { Icon: FileCheck, label: "Format",   value: "GIF · PNG · JPG",      hint: "No .mov · .mp4 · .webp · .svg" },
                  { Icon: Scale,     label: "Max size", value: "10 MB",                 hint: "Raw, uncompressed" },
                  { Icon: Film,      label: "Frames",   value: "50 or fewer",           hint: "Per GIF, not per second" },
                ].map(({ Icon, label, value, hint }) => (
                  <div key={label} className="px-6 py-4 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</p>
                      <p className="headline text-xl text-white leading-tight">{value}</p>
                      <p className="font-sans text-xs text-white/55 mt-0.5">{hint}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Hard rejects */}
              <div className="px-6 py-5 bg-red-500/10 border-t border-red-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle size={14} className="text-red-400" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-red-300">We will NOT accept</p>
                </div>
                <ul className="space-y-2">
                  {[
                    "Other formats (.mov, .mp4, .webp, .svg)",
                    "Files larger than 10 MB raw",
                    "GIFs with more than 50 frames",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-white/85">
                      <span className="w-1 h-1 rounded-full bg-red-400 mt-2.5 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Delivery note at the bottom */}
              <div className="px-6 py-4 border-t border-white/10 mt-auto">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">Delivery</p>
                <p className="font-sans text-sm text-white/70 leading-relaxed">{g.tech.responsive}</p>
              </div>
            </div>
          </FadeUp>
        )}
      </div>

      {/* Flexible package */}
      {showFlexible && (
        <FadeUp>
          <div className="mt-8 bg-primary text-primary-foreground rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4">
            <p className="headline text-2xl uppercase shrink-0">Flexible Package</p>
            <p className="text-base md:text-lg text-white/90">{g.flexible}</p>
          </div>
        </FadeUp>
      )}

      {/* Size tables */}
      <div className={`mt-12 grid grid-cols-1 ${showJumbo && g.jumbo && g.elegant ? "lg:grid-cols-2" : ""} gap-8`}>
        {[
          showJumbo && g.jumbo && { title: "Jumbo Profile", rows: g.jumbo, note: "Special premium size — tall creative" },
          g.elegant && { title: "Elegant Profile", rows: g.elegant, note: "Standard creative — Leaderboard + In Article positions" },
        ].filter(Boolean).map((t) => (
          <FadeUp key={t.title}>
            <div className="bg-white rounded-2xl border border-border overflow-hidden">
              <div className="p-5 border-b border-border">
                <p className="headline text-xl text-ink">{t.title}</p>
                <p className="text-sm text-foreground/60">{t.note}</p>
              </div>
              <table className="w-full text-left" data-testid={`ad-table-${t.title}`}>
                <thead>
                  <tr className="bg-secondary">
                    {["Size", "Upload (px)", "Display (px)", "Min. screen"].map((h) => (
                      <th key={h} className="px-4 py-3 font-sans text-[11px] font-extrabold uppercase tracking-wide text-foreground/50">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.rows.map((r) => (
                    <tr key={r.size} className="border-t border-border">
                      <td className="px-4 py-3 font-bold text-ink">{r.size}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{r.upload}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{r.display}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{r.minScreen === "0" ? "All screens" : `${r.minScreen}px`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
        ))}
      </div>

      <FadeUp>
        <Link to={`/contact?role=${encodeURIComponent(brandName + " Advertising")}`} className="mt-12 inline-flex items-center gap-2 font-sans text-sm font-bold bg-ink text-white rounded-full pl-6 pr-5 py-3.5 hover:bg-primary transition-colors">
          Enquire about advertising <ArrowRight size={16} />
        </Link>
      </FadeUp>
    </div>
  </section>
  );
};

export default AdGuidelines;
